import { OPTION_STATUS } from "@miplatform/constants";
import {
  IBidOptionMaterial,
  IBidOptionResponse,
  IMergedBidArea,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { resolveOptionArea } from "../../useConfigureOptionsViewModel";

export const useBidAreaOptionsViewModel = ({
  optionData,
  setOptionData,
}: UseBidAreaOptionsViewModelProps) => {
  // AREA OPTION HANDLERS

  const handleRemoveAreaFromOption = (area: IMergedBidArea) => {
    if (area.optionStatus === OPTION_STATUS.ADDED) {
      const updatedOptionAreas = optionData.planAreas.filter(
        (a) => a.id !== area.id
      );
      setOptionData((prev) => ({ ...prev, planAreas: updatedOptionAreas }));
      return;
    }

    // check if planArea already exists in the option
    const existingOptionArea = optionData.planAreas.find(
      (a) => a.referenceAreaId === area.id
    );
    const removedArea = {
      ...area,
      referenceAreaId: area.id,
      id: existingOptionArea?.id ?? createUUID(),
      optionStatus: OPTION_STATUS.REMOVED,
      // filter any added materials and set any existing materials to IsRemoved
      materials: (area.materials ?? [])
        .filter((m) => m.optionStatus !== OPTION_STATUS.ADDED)
        .map(
          (m): IBidOptionMaterial =>
            m.referencePlanMaterialId
              ? {
                  ...m,
                  optionStatus: OPTION_STATUS.REMOVED,
                }
              : {
                  ...m,
                  id: createUUID(),
                  referencePlanMaterialId: m.id,
                  optionStatus: OPTION_STATUS.REMOVED,
                }
        ),
    };

    // replace existing area if already in option
    const updatedPlanAreas = optionData.planAreas
      .filter((a) => a.id !== existingOptionArea?.id)
      .concat(removedArea);

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  const handleUndoRemoveAreaFromOption = (area: IMergedBidArea) => {
    const updatedPlanAreas = optionData.planAreas.filter(
      (a) => a.referenceAreaId !== area.id
    );

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  // ITEM OPTION HANDLERS

  const handleRemoveItemFromArea = (
    area: IMergedBidArea,
    material: IBidOptionMaterial
  ) => {
    const { existingOptionArea, resolvedArea } = resolveOptionArea(
      area,
      optionData.planAreas
    );
    const existingMaterial = existingOptionArea?.materials.find(
      (m) => m.id === material.id && m.optionStatus === material.optionStatus
    );

    const removedMaterial: IBidOptionMaterial = {
      ...material,
      optionStatus: OPTION_STATUS.REMOVED,
      ...(material.optionStatus !== OPTION_STATUS.ADDED && {
        referencePlanMaterialId:
          material.referencePlanMaterialId ?? material.id,
        id: existingMaterial?.id ?? createUUID(),
      }),
    };

    const updatedPlanAreas = existingOptionArea
      ? optionData.planAreas.map((a) => {
          // if the area is currently in the option, we need to update it (IsAdded or IsExisting)
          if (a.id !== resolvedArea.id && a.referenceAreaId !== area.id)
            return a;
          if (existingMaterial?.optionStatus === OPTION_STATUS.ADDED) {
            // if material exists and is added, remove it from the area
            return {
              ...a,
              materials: a.materials.filter(
                (m) => m.id !== existingMaterial.id
              ),
            };
          } // if not, add the removed material to the area
          return { ...a, materials: [...a.materials, removedMaterial] };
        })
      : // if the area does not exist in the option, add it with the removed material and its referenceAreaId
        [
          ...optionData.planAreas,
          { ...resolvedArea, materials: [removedMaterial] },
        ];

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  const handleRemoveAllItemsFromArea = (area: IMergedBidArea) => {
    const { existingOptionArea, resolvedArea } = resolveOptionArea(
      area,
      optionData.planAreas
    );

    const remainingExistingMaterials = (area.materials ?? []).filter(
      (m) =>
        m.optionStatus !== OPTION_STATUS.ADDED &&
        m.optionStatus !== OPTION_STATUS.REMOVED
    );

    // need to filter out any added materials, as well as convert any existing materials to IsRemoved
    const base = existingOptionArea
      ? {
          ...existingOptionArea,
          materials: [
            ...existingOptionArea.materials,
            ...remainingExistingMaterials,
          ],
        }
      : resolvedArea;

    // filter any added materials and set remaining materials to IsRemoved
    const materials = base.materials
      .filter((m) => m.optionStatus !== OPTION_STATUS.ADDED)
      .map((m): IBidOptionMaterial => {
        if (m.referencePlanMaterialId) {
          return {
            ...m,
            id: m.id ?? createUUID(),
            optionStatus: OPTION_STATUS.REMOVED,
          };
        }
        return {
          ...m,
          id: createUUID(),
          referencePlanMaterialId: m.id,
          optionStatus: OPTION_STATUS.REMOVED,
        };
      });

    const areaData = { ...base, materials };

    const updatedPlanAreas = existingOptionArea
      ? optionData.planAreas.map((a) =>
          a.id === resolvedArea.id || a.referenceAreaId === area.id
            ? areaData
            : a
        )
      : [...optionData.planAreas, areaData];

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  const handleReturnItemToPlan = (
    area: IMergedBidArea,
    material: IBidOptionMaterial
  ) => {
    const optionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );
    const remaining =
      optionArea?.materials.filter((m) => m.id !== material.id) ?? [];

    const updatedPlanAreas = remaining.length
      ? optionData.planAreas.map((a) =>
          a.id === area.id || a.referenceAreaId === area.id
            ? { ...a, materials: remaining }
            : a
        )
      : optionData.planAreas.filter(
          // if no materials left in the area, remove it from the option
          (a) => !(a.id === area.id || a.referenceAreaId === area.id)
        );

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  const handleReturnAllItemsToPlan = (area: IMergedBidArea) => {
    const optionArea = optionData.planAreas.find(
      (a) => a.id === area.id || a.referenceAreaId === area.id
    );
    const remaining =
      optionArea?.materials.filter(
        (m) => m.optionStatus !== OPTION_STATUS.REMOVED
      ) ?? [];

    const updatedPlanAreas = remaining.length
      ? optionData.planAreas.map((a) =>
          a.id === area.id || a.referenceAreaId === area.id
            ? { ...a, materials: remaining }
            : a
        )
      : optionData.planAreas.filter(
          (a) => !(a.id === area.id || a.referenceAreaId === area.id)
        );

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  const handleUpdateItemQuantity = (
    area: IMergedBidArea,
    materialId: string,
    quantity: number
  ) => {
    const updatedPlanAreas = optionData.planAreas.map((a) => {
      if (a.id !== area.id && a.referenceAreaId !== area.id) return a;
      return {
        ...a,
        materials: a.materials.map((m) =>
          m.materialId === materialId && m.optionStatus === OPTION_STATUS.ADDED
            ? { ...m, quantity }
            : m
        ),
      };
    });
    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  return {
    handleRemoveAreaFromOption,
    handleUndoRemoveAreaFromOption,
    handleRemoveItemFromArea,
    handleRemoveAllItemsFromArea,
    handleReturnItemToPlan,
    handleReturnAllItemsToPlan,
    handleUpdateItemQuantity,
  };
};

interface UseBidAreaOptionsViewModelProps {
  optionData: IBidOptionResponse;
  setOptionData: React.Dispatch<React.SetStateAction<IBidOptionResponse>>;
}
